import { getAlphabetForLanguage } from '../config/alphabets';
import { buildQuestionQueries, getQuestionAffixesForLanguage } from '../config/questions';
import type { CrawlProgress, CrawlSettings, KeywordData } from '../types/keyword.types';
import { fetchGoogleSuggestions } from './googleSuggestService';
import { isGluedYaSuggestion } from './suggestionFilter';

interface DiscoveredEntry {
  keyword: string;
  parent?: string;
  depth: number;
}

export interface CrawlParams {
  seed: string;
  language: string;
  country: string;
  settings: CrawlSettings;
  onProgress?: (progress: CrawlProgress) => void;
  onKeywordFound?: (keyword: KeywordData) => void;
  signal?: AbortSignal;
  /** When set, replaces direct Google JSONP (browser worker + query cache). */
  fetchSuggestions?: (
    query: string,
    progress: CrawlProgress,
  ) => Promise<string[]>;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function toKeywordRow(entry: DiscoveredEntry): KeywordData {
  return {
    keyword: entry.keyword,
    len: entry.keyword.trim().split(/\s+/).filter(Boolean).length,
    parent_keyword: entry.parent ?? '',
    source: 'google',
    sv: null,
    sv_trend: [],
    difficalty: null,
    intent: [],
    purpose: [],
    google_trends_trend: [],
    google_trends: null,
    depth: entry.depth,
  };
}

async function runPool<T>(
  items: T[],
  concurrency: number,
  worker: (item: T) => Promise<void>,
  signal?: AbortSignal
): Promise<void> {
  let index = 0;

  const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (index < items.length) {
      if (signal?.aborted) return;
      const current = items[index++];
      await worker(current);
    }
  });

  await Promise.all(runners);
}

export async function crawlGoogleKeywords({
  seed,
  language,
  country: _country,
  settings,
  onProgress,
  onKeywordFound,
  signal,
  fetchSuggestions,
}: CrawlParams): Promise<KeywordData[]> {
  const discovered = new Map<string, DiscoveredEntry>();
  const expanded = new Set<string>();
  let requestCount = 0;
  let currentPhase: CrawlProgress['phase'] = settings.enableQuestions
    ? 'questions'
    : 'alpha';

  const report = (phase: CrawlProgress['phase'], currentQuery?: string) => {
    currentPhase = phase;
    onProgress?.({
      phase,
      requestsDone: requestCount,
      keywordsFound: discovered.size,
      currentQuery,
    });
  };

  const limitsReached = () =>
    discovered.size >= settings.maxKeywords || requestCount >= settings.maxNodes;

  const addKeyword = (keyword: string, parent: string | undefined, depth: number): boolean => {
    const normalized = keyword.trim();
    if (!normalized || discovered.has(normalized) || discovered.size >= settings.maxKeywords) {
      return false;
    }

    const entry: DiscoveredEntry = { keyword: normalized, parent, depth };
    discovered.set(normalized, entry);
    onKeywordFound?.(toKeywordRow(entry));
    return true;
  };

  const fetchQuery = async (
    query: string,
    _parent: string | undefined,
    depth: number
  ): Promise<string[]> => {
    if (signal?.aborted || limitsReached()) return [];

    requestCount += 1;
    report(currentPhase, query);

    if (settings.delayMs > 0) {
      await sleep(settings.delayMs);
    }

    try {
      const suggestions = fetchSuggestions
        ? await fetchSuggestions(query, {
            phase: currentPhase,
            requestsDone: requestCount,
            keywordsFound: discovered.size,
            currentQuery: query,
          })
        : await fetchGoogleSuggestions(query);
      for (const suggestion of suggestions) {
        if (isGluedYaSuggestion(suggestion, query, trimmedSeed)) {
          continue;
        }
        addKeyword(suggestion, query, depth + 1);
      }
      return suggestions;
    } catch {
      return [];
    }
  };

  const trimmedSeed = seed.trim();

  if (settings.enableQuestions) {
    report('questions');
    const affixes = getQuestionAffixesForLanguage(language);
    const alphabet = getAlphabetForLanguage(language);
    const remainingNodes = settings.maxNodes - requestCount;
    const questionQueries = buildQuestionQueries(
      trimmedSeed,
      {
        suffixes: affixes.suffixes,
        prefixes: affixes.prefixes,
        prefixAlphabet: affixes.prefixAlphabet,
      },
      alphabet,
      remainingNodes > 0 ? remainingNodes : 0,
    );

    await runPool(
      questionQueries,
      settings.maxConcurrency,
      async (query) => {
        if (signal?.aborted || limitsReached()) return;
        await fetchQuery(query, query, 0);
      },
      signal,
    );
  }

  if (settings.enableAlpha) {
    report('alpha');
    addKeyword(trimmedSeed, undefined, 0);
    const alphabet = getAlphabetForLanguage(language);
    const alphaQueries = [trimmedSeed, ...alphabet.map((letter) => `${trimmedSeed} ${letter}`)];

    await runPool(
      alphaQueries,
      settings.maxConcurrency,
      async (query) => {
        if (signal?.aborted || limitsReached()) return;
        await fetchQuery(query, trimmedSeed, 0);
      },
      signal,
    );
  } else if (!settings.enableQuestions) {
    addKeyword(trimmedSeed, undefined, 0);
    await fetchQuery(trimmedSeed, undefined, 0);
  }

  // Phase 2: one nested pass on everything discovered so far
  if (settings.enableNested && !signal?.aborted) {
    report('nested');
    const snapshot = [...discovered.keys()].filter((keyword) => !expanded.has(keyword));

    await runPool(
      snapshot,
      settings.maxConcurrency,
      async (keyword) => {
        if (signal?.aborted || limitsReached()) return;
        const entry = discovered.get(keyword);
        if (!entry || expanded.has(keyword)) return;

        expanded.add(keyword);
        await fetchQuery(keyword, entry.parent, entry.depth);
      },
      signal
    );
  }

  // Phase 3: full BFS until limits
  if (!signal?.aborted) {
    report('full');
    const queue = [...discovered.keys()].filter((keyword) => !expanded.has(keyword));

    while (queue.length > 0 && !limitsReached() && !signal?.aborted) {
      const batch = queue.splice(0, settings.maxConcurrency);

      await runPool(
        batch,
        settings.maxConcurrency,
        async (keyword) => {
          if (signal?.aborted || limitsReached()) return;

          const entry = discovered.get(keyword);
          if (!entry || expanded.has(keyword)) return;

          if (entry.depth >= settings.maxDepth) {
            expanded.add(keyword);
            return;
          }

          expanded.add(keyword);
          const suggestions = await fetchQuery(keyword, entry.parent, entry.depth);

          if (settings.enableNested) {
            for (const suggestion of suggestions) {
              const child = discovered.get(suggestion);
              if (child && !expanded.has(suggestion) && !queue.includes(suggestion)) {
                queue.push(suggestion);
              }
            }
          }
        },
        signal
      );
    }
  }

  report('done');
  return [...discovered.values()].map(toKeywordRow);
}
